import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { createSupabaseBrowserClient } from '@/lib/supabase/client';
import { AdminSidebar } from '@/components/admin/sidebar';

type AdminProfile = { id: string; email: string; full_name: string; role: string; status: string };

type AuthState =
  | { status: 'checking' }
  | { status: 'authorized'; profile: AdminProfile }
  | { status: 'denied' };

export default function RequireAuth() {
  const [state, setState] = useState<AuthState>({ status: 'checking' });
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const supabase = createSupabaseBrowserClient();

    (async () => {
      const { data: userRes, error: userErr } = await supabase.auth.getUser();
      if (cancelled) return;
      if (userErr || !userRes?.user) {
        setState({ status: 'denied' });
        return;
      }
      const { data: profile, error: profileErr } = await supabase
        .from('admin_users')
        .select('id, email, full_name, role, status')
        .eq('id', userRes.user.id)
        .maybeSingle();

      if (cancelled) return;
      if (profileErr || !profile || profile.status !== 'active') {
        await supabase.auth.signOut();
        setState({ status: 'denied' });
        return;
      }
      setState({ status: 'authorized', profile: profile as AdminProfile });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Verificando sesión...</p>
      </div>
    );
  }
  if (state.status === 'denied') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/admin/login?next=${next}`} replace />;
  }

  const { profile } = state;
  return (
    <div className="flex min-h-screen bg-secondary/30">
      <AdminSidebar user={{ email: profile.email, full_name: profile.full_name ?? '', role: profile.role }} />
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl px-4 pb-8 pt-20 sm:px-6 sm:pt-24 lg:p-8">
          <Outlet context={{ profile }} />
        </div>
      </div>
    </div>
  );
}
